import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { motion } from 'framer-motion'
import {
  Zap,
  Lock,
  Unlock,
  Star,
  Clock,
  Trophy,
  TrendingUp,
  AlertTriangle,
  Flame,
  Snowflake,
  Radiation,
  Droplets,
  Shield,
  Brain,
  Users,
  ArrowLeft
} from 'lucide-react'
import { useSimulations, useSimulationHistory } from '../hooks/useAPI'
import toast from 'react-hot-toast'
import Footer from '../components/Footer'
import '../styles/simulationHub.css'

/**
 * SimulationHub Component
 * Story mode page with unlockable survival scenarios
 */

const scenarioIcons = {
  'nuclear': Radiation,
  'blackout': Zap,
  'chemical': Droplets,
  'winter': Snowflake,
  'fire': Flame,
  'pandemic': Shield,
  'civil': Users,
  'cyber': Brain
}

const SimulationCard = ({ simulation, userScore, isUnlocked, onStart, navigate }) => {
  const Icon = scenarioIcons[simulation.category] || AlertTriangle
  const [isHovered, setIsHovered] = useState(false)

  const cardStateClass = isUnlocked ? 'unlocked' : 'locked'

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.3 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className={`simulation-card ${cardStateClass}`}
    >
      {/* Background Pattern */}
      <div className="card-ambient">
        <div className="card-gradient" />
      </div>

      {/* Lock Overlay */}
      {!isUnlocked && (
        <div className="card-locked">
          <motion.div
            animate={{ scale: isHovered ? 1.2 : 1 }}
            transition={{ duration: 0.3 }}
          >
            <Lock className="w-12 h-12 text-gray-500" />
          </motion.div>
        </div>
      )}

      {/* Content */}
      <div className="card-content">
        {/* Header */}
        <div className="card-header">
          <div className="card-title-block">
            <div className={`icon-chip ${cardStateClass}`}>
              <Icon className={`w-6 h-6 ${isUnlocked ? 'text-orange-400' : 'text-gray-600'}`} />
            </div>
            <div>
              <h3 className={`simulation-title ${cardStateClass}`}>{simulation.title}</h3>
              <div className="card-meta">
                <span className="pill pill-muted">{simulation.difficulty}</span>
                <span className="dot-divider">•</span>
                <Clock className="w-3 h-3 text-gray-400" />
                <span className="text-sm text-gray-400">{simulation.estimatedTime} min</span>
              </div>
            </div>
          </div>
          
          {/* Status Badge */}
          {userScore ? (
            <div className="status-block">
              <div className="pill pill-success">
                <Trophy className="w-3 h-3" />
                <span>Completed</span>
              </div>
              <span className="status-sub">Score: {userScore}%</span>
            </div>
          ) : isUnlocked ? (
            <div className="pill pill-warm">Ready to Play</div>
          ) : (
            <div className="status-locked">Requires {simulation.requiredXP} XP</div>
          )}
        </div>

        {/* Description */}
        <p className={`card-description ${cardStateClass}`}>{simulation.description}</p>

        {/* Stats */}
        {isUnlocked && (
          <>
            <div className="card-stats">
              <div className="stat-chip">
                <Star className="w-4 h-4 text-yellow-500" />
                <span className="text-sm text-gray-400">{simulation.ratings || '4.8'}</span>
              </div>
              <div className="stat-chip">
                <Users className="w-4 h-4 text-blue-400" />
                <span className="text-sm text-gray-400">{simulation.playCount || '2.3k'} plays</span>
              </div>
              {simulation.isNew && (
                <span className="pill pill-new">NEW</span>
              )}
            </div>
            
            {/* Play Buttons */}
            <div className="card-actions">
              <button onClick={() => onStart(simulation.id)} className="btn btn-primary w-full">
                Play Solo
              </button>
              <button
                onClick={() => navigate(`/2player/${simulation.id}`)}
                className="btn btn-secondary w-full"
              >
                <Users className="w-4 h-4" />
                2-Player
              </button>
            </div>
          </>
        )}
      </div>
    </motion.div>
  )
}

const SimulationHub = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [filter, setFilter] = useState('all')

  // Fetch simulations and history from backend
  const { data: simulationsData, isLoading: simulationsLoading } = useSimulations()
  const { data: historyData, isLoading: historyLoading } = useSimulationHistory()

  const loading = simulationsLoading || historyLoading

  // Parse simulations and user scores from API response
  const simulations = simulationsData?.simulations || []

  // Create userScores map from history
  const userScores = {}
  if (historyData?.history) {
    historyData.history.forEach(record => {
      // Store the best score for each simulation
      if (!userScores[record.simulation_id] || record.score > userScores[record.simulation_id]) {
        userScores[record.simulation_id] = record.score
      }
    })
  }

  const isSimulationUnlocked = (simulation) => {
    return (user?.xp || 0) >= simulation.requiredXP
  }

  const handleStartSimulation = (simulationId) => {
    navigate(`/simulation/${simulationId}`)
  }

  const filteredSimulations = simulations.filter(sim => {
    if (filter === 'all') return true
    if (filter === 'unlocked') return isSimulationUnlocked(sim)
    if (filter === 'completed') return userScores[sim.id]
    if (filter === 'new') return sim.isNew
    return true
  })

  if (loading) {
    return (
      <div className="hub-shell">
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading simulations...</p>
        </div>
      </div>
    )
  }

  const avgScore = Object.keys(userScores).length
    ? Math.round(Object.values(userScores).reduce((a, b) => a + b, 0) / Object.keys(userScores).length)
    : 0

  return (
    <div className="hub-shell">
      <div className="landing-ambient" />

      <div className="hub-hero">
        <div className="container">
          <motion.button
            className="back-button"
            onClick={() => navigate('/')}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <ArrowLeft size={20} />
            <span>Back to Home</span>
          </motion.button>

          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="hero-copy"
          >
            <span className="eyebrow">Practice Makes Perfect</span>
            <h1 className="text-4xl font-bold text-white mb-3">
              Simulation Hub
            </h1>
            <p className="text-gray-300 text-lg">
              Test your survival skills in realistic scenarios and make decisions under pressure.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="hub-stats"
          >
            <div className="stat-card">
              <TrendingUp className="w-6 h-6 text-green-400" />
              <div className="stat-number">{Object.keys(userScores).length}</div>
              <div className="stat-label">Completed</div>
            </div>
            <div className="stat-card">
              <Trophy className="w-6 h-6 text-yellow-400" />
              <div className="stat-number">{avgScore}%</div>
              <div className="stat-label">Avg Score</div>
            </div>
            <div className="stat-card">
              <Unlock className="w-6 h-6 text-orange-400" />
              <div className="stat-number">{simulations.filter(s => isSimulationUnlocked(s)).length}</div>
              <div className="stat-label">Unlocked</div>
            </div>
          </motion.div>

          <div className="filter-row">
            {['all', 'unlocked', 'completed', 'new'].map((filterOption) => (
              <button
                key={filterOption}
                onClick={() => setFilter(filterOption)}
                className={`filter-chip ${filter === filterOption ? 'active' : ''}`}
              >
                {filterOption}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="container">
        <div className="sim-grid">
          {filteredSimulations.map((simulation, index) => (
            <motion.div
              key={simulation.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <SimulationCard
                simulation={simulation}
                userScore={userScores[simulation.id]}
                isUnlocked={isSimulationUnlocked(simulation)}
                onStart={handleStartSimulation}
                navigate={navigate}
              />
            </motion.div>
          ))}
        </div>

        {filteredSimulations.length === 0 && (
          <div className="empty-state">
            <p>No simulations found for this filter</p>
          </div>
        )}
      </div>

      <Footer />
    </div>
  )
}

export default SimulationHub
